/**
 * Non-color design tokens and the translucent (rgba) color tokens. Opaque color tokens live in
 * `src/theme.ts` (`tokens`), where `scripts/check-contrast.ts` parses them.
 * Source: `specs/002-portal-design-theme/contracts/design-tokens.md`.
 */
import type { ConsoleMode } from "../theme";

/** `theme.spacing(1)` in px (MUI default); design paddings like 6/10/14 px are half-steps. */
export const space = 8;
export const contentMaxWidth = 1360;
export const headerHeight = 64;
/**
 * Height of the tricolor header rule (the contract's header-rule token). Renamed because
 * `scripts/check-no-legacy.sh` bans the contract's original name (legacy payments provider).
 */
export const ruleHeight = 3;

export const radius = {
  pill: 999,
  /** Hero band, reveal card and dialogs. */
  hero: 22,
  dialog: 22,
  card: 18,
  panel: 14,
  control: 12,
  menuItem: 8,
  mark: 3,
} as const;

export const elevation = {
  card: {
    light: "0 1px 0 #E3E7F0, 0 8px 24px -16px rgba(10,26,58,.25)",
    dark: "0 0 0 1px #1D2B52",
  },
  menu: "0 18px 40px -12px rgba(10,26,58,.35)",
  glowPresidential: {
    light: "none",
    dark: "0 0 14px rgba(61,107,255,.4)",
  },
  glowNational: {
    light: "none",
    dark: "0 0 14px rgba(240,54,79,.32)",
  },
} as const;

/** Transition durations in ms. Zeroed under reduced motion by `buildTheme`. */
export const motion = { fast: 200, base: 250, slow: 500 } as const;

export const focus = { width: 2, offset: 2 } as const;

/** Minimum hit area for station controls, in px. */
export const touchTarget = 48;

/** Dialog backdrop (translucent; exempt from contrast checks). */
export const overlay: Record<ConsoleMode, string> = {
  light: "rgba(10,26,58,.45)",
  dark: "rgba(0,0,0,.62)",
};

/** Translucent panel on the hero band. */
export const heroPanel: Record<ConsoleMode, string> = {
  light: "rgba(10,26,58,.35)",
  dark: "rgba(5,11,28,.6)",
};

/** Dot grid drawn over hero surfaces (design `--dots`). */
export const heroDots: Record<ConsoleMode, string> = {
  light: "rgba(255,255,255,.16)",
  dark: "rgba(61,107,255,.22)",
};

/** Opacity of the diagonal band block on hero surfaces (design `--band-op`). */
export const heroBandOpacity: Record<ConsoleMode, number> = {
  light: 0.95,
  dark: 0.55,
};

/** Decorative hairline on the hero band. */
export const heroLine: Record<ConsoleMode, string> = {
  light: "rgba(255,255,255,.5)",
  dark: "#2A3A66",
};

/** Print output is always ink on white, whatever the screen mode. */
export const print = { ink: "#0A1A3A", paper: "#FFFFFF" } as const;
